import React from 'react';
import { useSelector } from 'react-redux';
import { Grid, List, ListItem, Typography, Tooltip } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { formatDisplayValue } from './NodeCard';
import { isFieldVisible } from '../Interfaces.shared';

const DicomSendingNodeDetails = ({ node, config, t, classes }) => {
  const theme = useTheme();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5} style={{ borderRight: `1px solid ${theme.palette.divider}` }}>
        <Typography className={classes.listHeader}>{t('Settings.node_details')}</Typography>
        <List className={classes.listContainer}>
          <ListItem className={classes.listTitleItem}>
            <Grid container>
              <Grid item xs={4} className={classes.nodeTitles}>
                {t('Settings.aet')}
              </Grid>
              <Grid item xs={8} className={classes.nodeValues}>
                {node.aet}
              </Grid>
            </Grid>
          </ListItem>
          <ListItem className={classes.listTitleItem}>
            <Grid container>
              <Grid item xs={4} className={classes.nodeTitles}>
                {t('Settings.ip')}
              </Grid>
              <Grid item xs={8} className={classes.nodeValues}>
                {node.ip}
              </Grid>
            </Grid>
          </ListItem>
          <ListItem className={classes.listTitleItem}>
            <Grid container>
              <Grid item xs={4} className={classes.nodeTitles}>
                {t('Settings.port')}
              </Grid>
              <Grid item xs={8} className={classes.nodeValues}>
                {node.port}
              </Grid>
            </Grid>
          </ListItem>
          <ListItem className={classes.listTitleItem}>
            <Typography className={classes.nodeTitles} style={{ marginBottom: 8 }}>
              {t('Settings.institutions_served')}
            </Typography>
            <List className={classes.listContainer} style={{ paddingLeft: '4px' }}>
              {Array.isArray(node.institutions) && node.institutions.length > 0 ? (
                node.institutions.map((institution, idx) => (
                  <ListItem
                    key={`${node.name}-institution-${idx}`}
                    style={{ padding: 0, width: '100%' }}
                    className={classes.nodeValues}>
                    {`- ${institution}`}
                  </ListItem>
                ))
              ) : (
                <ListItem style={{ padding: 0, width: '100%' }}>
                  <Typography className={classes.nodeValues}>{t('ResultView.all')}</Typography>
                </ListItem>
              )}
            </List>
          </ListItem>
        </List>
      </Grid>
      <Grid item xs={12} md={7}>
        <Typography className={classes.listHeader}>{t('Settings.report_sending_rules')}</Typography>
        <List>
          <ListItem className={classes.tableTitleItemsContainer} style={{ padding: 2 }}>
            <Grid container>
              <Grid item xs={2} className={classes.tableListHeader}>
                {t('Settings.report_type')}
              </Grid>
              <Grid item xs={4} className={classes.tableListHeader}>
                {t('Settings.report_subtype')}
              </Grid>
              <Grid item xs={3} className={classes.tableListHeader} style={{ textAlign: 'center' }}>
                {t('Settings.report_mode')}
              </Grid>
              <Grid item xs={2} className={classes.tableListHeader} style={{ textAlign: 'center' }}>
                {t('Settings.vendor')} / {t('Settings.tag_modality')}
              </Grid>
              <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center' }} />
            </Grid>
          </ListItem>
          {node?.sending_rules?.length > 0 ? (
            node?.sending_rules?.map((report, idx) => (
              <ListItem
                key={`${node.name}report-${idx}`}
                className={classes.listItem}
                style={{ padding: '6px 0 6px 2px' }}>
                <Grid container>
                  <Grid item xs={2}>
                    {report?.type || '—'}
                  </Grid>
                  <Grid item xs={4}>
                    {t(`Settings.${report?.subtype}`) || '—'}
                  </Grid>
                  <Grid item xs={3} style={{ display: 'flex', justifyContent: 'center' }}>
                    {report?.mode ? (
                      <div
                        className={classes.chipMode}
                        style={{
                          background: String(report.mode).toLowerCase() === 'manual' ? '#635858' : '#6f5d5d',
                        }}>
                        {t(`Settings.${String(report.mode).toLowerCase()}`)}
                      </div>
                    ) : (
                      <div className={classes.chipMode}>—</div>
                    )}
                  </Grid>
                  <Grid item xs={2} className={classes.chipMode} style={{ textTransform: 'uppercase' }}>
                    {report?.type === 'SR' ? report?.vendor || '—' : report?.modality_tag || '—'}
                  </Grid>
                  <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip
                      title={
                        <Typography variant='body1'>
                          {report?.is_active === 1 ? t('Settings.rule_active') : t('Settings.rule_inactive')}
                        </Typography>
                      }>
                      {report?.is_active === 1 ? (
                        <CheckCircleIcon fontSize='small' color='secondary' />
                      ) : (
                        <HighlightOffIcon fontSize='small' color='disabled' />
                      )}
                    </Tooltip>
                  </Grid>
                </Grid>
              </ListItem>
            ))
          ) : (
            <ListItem className={classes.listItem} style={{ padding: '6px 0 6px 2px' }}>
              <Typography className={classes.nodeValues}>{t('Settings.no_rules_found')}</Typography>
            </ListItem>
          )}
        </List>
      </Grid>
    </Grid>
  );
};

export const renderDicomSendingNodeDetails = (node, config, t, classes) => {
  return <DicomSendingNodeDetails node={node} config={config} t={t} classes={classes} />;
};

const WorklistNodeDetails = ({ node, config, t, classes }) => {
  const dicomServerAet = useSelector((state) => state.global_settings?.dicom_server_aet) || '';
  const hiddenFields = new Set([
    'name',
    ...(config.headerToggles || []),
    ...(config.hiddenDetailFields || []),
  ]);
  const detailFields = (config.detailFields || config.fields || []).filter(
    (field) => !hiddenFields.has(field.key) && isFieldVisible(field, node),
  );

  return (
    <div className={classes.nodeDetailsContainer}>
      <div className={classes.nodeDetailsItems}>
        {detailFields.map((field) => (
          <div key={field.key} className={classes.nodeDetailsItem}>
            <Typography className={classes.nodeTitles} style={{ width: '150px' }}>
              {t(field.labelKey)}
            </Typography>
            <Typography className={classes.nodeValues}>
              {formatDisplayValue(node, field, t, { dicom_server_aet: dicomServerAet })}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

export const renderWorklistNodeDetails = (node, config, t, classes) => {
  return <WorklistNodeDetails node={node} config={config} t={t} classes={classes} />;
};

export const DETAIL_RENDERERS = {
  dicomSending: renderDicomSendingNodeDetails,
  worklist: renderWorklistNodeDetails,
};
